import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import {
  ACCESS_DENIED_MESSAGE,
  formatKnowledgeDateTime,
  formatRepoDisplay,
  hasActiveRefreshJob,
  isRepoConfigured,
  isRepoReadyForSync,
  JOBS_LIST_LIMIT,
  MANIFEST_PAGE_SIZE,
  ACTIVE_SYNC_JOB_MESSAGE,
  QUEUED_REFRESH_MESSAGE,
  resolveScanKind,
  resolveSyncButtonLabel,
  shortSha,
  snapshotMetadataValue,
} from './automationKnowledgeUtils';
import { mapRepoConfigResponse } from './Settings/automationRepoConfigUtils';

const CLASSIFICATION_FILTERS = [
  { value: '', label: 'All classifications' },
  { value: 'test_file', label: 'test_file' },
  { value: 'config', label: 'config' },
  { value: 'support', label: 'support' },
  { value: 'excluded', label: 'excluded' },
];

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'active' },
  { value: 'changed', label: 'changed' },
  { value: 'added', label: 'added' },
  { value: 'removed', label: 'removed' },
];

function VerificationChip({ repoConfig }) {
  if (!isRepoConfigured(repoConfig)) {
    return <Chip size="small" label="Not configured" />;
  }
  if (repoConfig.verified) {
    return <Chip size="small" color="success" label="Verified" />;
  }
  if (repoConfig.verification_required) {
    return <Chip size="small" color="warning" label="Verification required" />;
  }
  return <Chip size="small" label="Not verified" />;
}

const AutomationKnowledge = ({ selectedProject, apiClient, onNavigateToSettings }) => {
  const projectId = selectedProject?.project_id;
  const [repoConfig, setRepoConfig] = useState(null);
  const [repoLoading, setRepoLoading] = useState(false);
  const [repoError, setRepoError] = useState(null);
  const [latestSnapshot, setLatestSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [manifest, setManifest] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [manifestClassification, setManifestClassification] = useState('');
  const [manifestStatus, setManifestStatus] = useState('');
  const [manifestOffset, setManifestOffset] = useState(0);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [actionError, setActionError] = useState(null);

  const repoConfigId = repoConfig?.repo_config_id || null;
  const snapshotId = latestSnapshot?.snapshot_id || null;
  const activeJobExists = useMemo(() => hasActiveRefreshJob(jobs), [jobs]);
  const syncReady = isRepoReadyForSync(repoConfig);
  const syncDisabled = !projectId || !syncReady || syncLoading || activeJobExists || accessDenied;

  const loadRepoConfig = useCallback(async () => {
    if (!projectId || !apiClient?.getAutomationRepoConfig) {
      setRepoConfig(null);
      return;
    }
    setRepoLoading(true);
    setRepoError(null);
    try {
      const response = await apiClient.getAutomationRepoConfig(projectId);
      setRepoConfig(mapRepoConfigResponse(response));
    } catch (err) {
      setRepoConfig(null);
      setRepoError(err?.message || 'Failed to load automation repository configuration.');
    } finally {
      setRepoLoading(false);
    }
  }, [projectId, apiClient]);

  const loadLatestSnapshot = useCallback(async () => {
    if (!projectId || !repoConfigId || !apiClient?.getAutomationKnowledgeLatestSnapshot) {
      setLatestSnapshot(null);
      return;
    }
    setSnapshotLoading(true);
    setAccessDenied(false);
    try {
      const response = await apiClient.getAutomationKnowledgeLatestSnapshot(projectId, repoConfigId);
      setLatestSnapshot(response);
    } catch (err) {
      setLatestSnapshot(null);
      if (err?.status === 404) {
        return;
      }
      if (err?.status === 403) {
        setAccessDenied(true);
        return;
      }
      setActionError(err?.message || 'Failed to load latest snapshot.');
    } finally {
      setSnapshotLoading(false);
    }
  }, [projectId, repoConfigId, apiClient]);

  const loadJobs = useCallback(async () => {
    if (!projectId || !repoConfigId || !apiClient?.listAutomationKnowledgeJobs) {
      setJobs([]);
      return;
    }
    setJobsLoading(true);
    try {
      const response = await apiClient.listAutomationKnowledgeJobs(projectId, {
        repoConfigId,
        limit: JOBS_LIST_LIMIT,
      });
      setJobs(response.jobs || []);
    } catch (err) {
      setJobs([]);
      if (err?.status === 403) {
        setAccessDenied(true);
        return;
      }
      setActionError(err?.message || 'Failed to load refresh jobs.');
    } finally {
      setJobsLoading(false);
    }
  }, [projectId, repoConfigId, apiClient]);

  const loadManifest = useCallback(async () => {
    if (!projectId || !snapshotId || !apiClient?.listAutomationKnowledgeManifest) {
      setManifest(null);
      return;
    }
    setManifestLoading(true);
    try {
      const response = await apiClient.listAutomationKnowledgeManifest(projectId, snapshotId, {
        classification: manifestClassification || undefined,
        status: manifestStatus || undefined,
        limit: MANIFEST_PAGE_SIZE,
        offset: manifestOffset,
      });
      setManifest(response);
    } catch (err) {
      setManifest(null);
      if (err?.status === 403) {
        setAccessDenied(true);
        return;
      }
      setActionError(err?.message || 'Failed to load manifest rows.');
    } finally {
      setManifestLoading(false);
    }
  }, [
    projectId,
    snapshotId,
    apiClient,
    manifestClassification,
    manifestStatus,
    manifestOffset,
  ]);

  useEffect(() => {
    setRepoConfig(null);
    setLatestSnapshot(null);
    setJobs([]);
    setManifest(null);
    setSyncMessage(null);
    setActionError(null);
    setAccessDenied(false);
    setManifestOffset(0);
    loadRepoConfig();
  }, [projectId, loadRepoConfig]);

  useEffect(() => {
    setLatestSnapshot(null);
    setJobs([]);
    setManifest(null);
    setManifestOffset(0);
    if (repoConfigId) {
      loadLatestSnapshot();
      loadJobs();
    }
  }, [repoConfigId, loadLatestSnapshot, loadJobs]);

  useEffect(() => {
    if (snapshotId) {
      loadManifest();
    } else {
      setManifest(null);
    }
  }, [snapshotId, loadManifest]);

  const handleSync = async () => {
    if (!projectId || !repoConfigId || !apiClient?.enqueueAutomationKnowledgeRefresh) {
      return;
    }
    setSyncLoading(true);
    setSyncMessage(null);
    setActionError(null);
    try {
      await apiClient.enqueueAutomationKnowledgeRefresh(projectId, {
        repoConfigId,
        scanKind: resolveScanKind(latestSnapshot),
      });
      setSyncMessage(QUEUED_REFRESH_MESSAGE);
      await loadJobs();
    } catch (err) {
      if (err?.status === 403) {
        setAccessDenied(true);
        return;
      }
      setActionError(err?.message || 'Failed to queue refresh job.');
    } finally {
      setSyncLoading(false);
    }
  };

  if (!projectId) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }} data-testid="automation-knowledge-page">
        <Typography variant="body1">Select a project to manage Automation Knowledge.</Typography>
      </Paper>
    );
  }

  return (
    <Box data-testid="automation-knowledge-page">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Automation Knowledge</Typography>
        <Typography variant="body2" color="text.secondary">
          Repository-discovered automation metadata
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This view shows repository metadata only. Source code contents are not stored or displayed.
          Repository metadata sync jobs remain queued until the Automation Knowledge refresh worker is enabled.
          Knowledge generation is a separate future step.
        </Typography>
      </Box>

      {accessDenied ? (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="automation-knowledge-access-denied">
          {ACCESS_DENIED_MESSAGE}
        </Alert>
      ) : null}

      {syncMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncMessage(null)} data-testid="automation-knowledge-sync-success">
          {syncMessage}
        </Alert>
      ) : null}

      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }} data-testid="automation-knowledge-repo-card">
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Automation repository</Typography>
        {repoLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} data-testid="automation-knowledge-repo-loading" />
          </Box>
        ) : repoError ? (
          <Alert severity="error">{repoError}</Alert>
        ) : !isRepoConfigured(repoConfig) ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Configure an automation repository before syncing Automation Knowledge.
            </Typography>
            {onNavigateToSettings ? (
              <Button variant="outlined" onClick={onNavigateToSettings} data-testid="automation-knowledge-open-settings">
                Open Automation Settings
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Open Settings → Configuration → Automation to configure the repository.
              </Typography>
            )}
          </Box>
        ) : (
          <Stack spacing={1}>
            <Typography variant="body2" data-testid="automation-knowledge-repo-display">
              <strong>Repository:</strong> {formatRepoDisplay(repoConfig)}
            </Typography>
            <Typography variant="body2">
              <strong>Branch:</strong> {repoConfig.base_branch}
            </Typography>
            <Typography variant="body2">
              <strong>Automation base path:</strong> {repoConfig.automation_base_path}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <VerificationChip repoConfig={repoConfig} />
              {repoConfig.credential_type ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Credential: ${repoConfig.credential_type}${repoConfig.credential_status ? ` (${repoConfig.credential_status})` : ''}`}
                />
              ) : null}
            </Stack>
            {repoConfig.last_verified_at ? (
              <Typography variant="caption" color="text.secondary">
                Last verified {formatKnowledgeDateTime(repoConfig.last_verified_at)}
              </Typography>
            ) : null}
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }} data-testid="automation-knowledge-snapshot-card">
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Latest snapshot</Typography>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={handleSync}
            disabled={syncDisabled}
            data-testid="automation-knowledge-sync-button"
          >
            {resolveSyncButtonLabel(latestSnapshot)}
          </Button>
        </Stack>

        {activeJobExists ? (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="automation-knowledge-active-job-notice">
            {ACTIVE_SYNC_JOB_MESSAGE}
          </Alert>
        ) : null}

        {snapshotLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} data-testid="automation-knowledge-snapshot-loading" />
          </Box>
        ) : latestSnapshot ? (
          <Stack spacing={1} data-testid="automation-knowledge-snapshot-details">
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip size="small" label={`Status: ${latestSnapshot.status}`} />
              <Chip size="small" label={`Scan: ${latestSnapshot.scan_kind}`} />
              <Chip size="small" label={`Branch: ${latestSnapshot.branch}`} />
            </Stack>
            <Typography variant="body2">
              <strong>Commit:</strong> {shortSha(latestSnapshot.commit_sha)}
            </Typography>
            <Typography variant="body2">
              <strong>Fetched:</strong> {formatKnowledgeDateTime(latestSnapshot.fetched_at)}
            </Typography>
            <Typography variant="body2" data-testid="automation-knowledge-snapshot-counters">
              Files — total {latestSnapshot.file_count_total}, included {latestSnapshot.file_count_included},
              excluded {latestSnapshot.file_count_excluded}, added {latestSnapshot.file_count_added},
              changed {latestSnapshot.file_count_changed}, deleted {latestSnapshot.file_count_deleted}
            </Typography>
            {snapshotMetadataValue(latestSnapshot, 'scanner_version') ? (
              <Typography variant="body2">
                <strong>Scanner version:</strong> {snapshotMetadataValue(latestSnapshot, 'scanner_version')}
              </Typography>
            ) : null}
            {snapshotMetadataValue(latestSnapshot, 'secret_denied_count') != null ? (
              <Typography variant="body2">
                <strong>Secret-denied paths:</strong> {snapshotMetadataValue(latestSnapshot, 'secret_denied_count')}
              </Typography>
            ) : null}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" data-testid="automation-knowledge-snapshot-empty">
            No repository metadata has been synced yet. Sync the automation repository to queue the first scan.
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }} data-testid="automation-knowledge-jobs-card">
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Recent refresh jobs</Typography>
        {jobsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} data-testid="automation-knowledge-jobs-loading" />
          </Box>
        ) : jobs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" data-testid="automation-knowledge-jobs-empty">
            No refresh jobs yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" data-testid="automation-knowledge-jobs-table">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Scan kind</TableCell>
                  <TableCell>Queued</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Snapshot</TableCell>
                  <TableCell>Failure</TableCell>
                  <TableCell>Credential</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id} data-testid={`automation-knowledge-job-row-${job.id}`}>
                    <TableCell>{job.status}</TableCell>
                    <TableCell>{job.scan_kind}</TableCell>
                    <TableCell>{formatKnowledgeDateTime(job.queued_at)}</TableCell>
                    <TableCell>{formatKnowledgeDateTime(job.started_at)}</TableCell>
                    <TableCell>{formatKnowledgeDateTime(job.completed_at)}</TableCell>
                    <TableCell>{shortSha(job.result_snapshot_id, 8)}</TableCell>
                    <TableCell>{job.failure_category || job.safe_error || '—'}</TableCell>
                    <TableCell>{job.credential_type || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {latestSnapshot ? (
        <Paper variant="outlined" sx={{ p: 2.5 }} data-testid="automation-knowledge-manifest-card">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Manifest</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="manifest-classification-label">Classification</InputLabel>
              <Select
                labelId="manifest-classification-label"
                label="Classification"
                value={manifestClassification}
                onChange={(event) => {
                  setManifestClassification(event.target.value);
                  setManifestOffset(0);
                }}
                data-testid="automation-knowledge-manifest-classification"
              >
                {CLASSIFICATION_FILTERS.map((option) => (
                  <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="manifest-status-label">Status</InputLabel>
              <Select
                labelId="manifest-status-label"
                label="Status"
                value={manifestStatus}
                onChange={(event) => {
                  setManifestStatus(event.target.value);
                  setManifestOffset(0);
                }}
                data-testid="automation-knowledge-manifest-status"
              >
                {STATUS_FILTERS.map((option) => (
                  <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {manifestLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} data-testid="automation-knowledge-manifest-loading" />
            </Box>
          ) : !manifest?.entries?.length ? (
            <Typography variant="body2" color="text.secondary" data-testid="automation-knowledge-manifest-empty">
              No manifest rows for the latest snapshot.
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small" data-testid="automation-knowledge-manifest-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>File path</TableCell>
                      <TableCell>Classification</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Blob SHA</TableCell>
                      <TableCell>Previous blob SHA</TableCell>
                      <TableCell>First seen</TableCell>
                      <TableCell>Last seen</TableCell>
                      <TableCell>Removed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {manifest.entries.map((entry) => (
                      <TableRow key={entry.id} data-testid={`automation-knowledge-manifest-row-${entry.id}`}>
                        <TableCell>{entry.file_path}</TableCell>
                        <TableCell>{entry.classification}</TableCell>
                        <TableCell>{entry.status}</TableCell>
                        <TableCell>{entry.size_bytes}</TableCell>
                        <TableCell>{shortSha(entry.blob_sha)}</TableCell>
                        <TableCell>{shortSha(entry.previous_blob_sha)}</TableCell>
                        <TableCell>{formatKnowledgeDateTime(entry.first_seen_at)}</TableCell>
                        <TableCell>{formatKnowledgeDateTime(entry.last_seen_at)}</TableCell>
                        <TableCell>{formatKnowledgeDateTime(entry.removed_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={manifestOffset <= 0}
                  onClick={() => setManifestOffset((value) => Math.max(0, value - MANIFEST_PAGE_SIZE))}
                  data-testid="automation-knowledge-manifest-prev"
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={(manifestOffset + MANIFEST_PAGE_SIZE) >= (manifest?.total || 0)}
                  onClick={() => setManifestOffset((value) => value + MANIFEST_PAGE_SIZE)}
                  data-testid="automation-knowledge-manifest-next"
                >
                  Next
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      ) : null}
    </Box>
  );
};

export default AutomationKnowledge;
